import React, { useMemo } from "react";
import { cn } from "@/lib/utils";
import { isSafeUrl } from "@/lib/utils/urlUtils";

interface LinkifiedTextProps {
  text: string;
  className?: string;
  linkClassName?: string;
}

const NEWLINE_REGEX = /\r\n|\r|\n/;

const renderTextSegment = (
  segment: string,
  keyPrefix: string,
): React.ReactNode[] => {
  if (!segment) {
    return [];
  }

  const lines = segment.split(NEWLINE_REGEX);
  return lines.flatMap((line, index) => {
    const key = `${keyPrefix}-${index}`;

    if (index === 0) {
      return [<React.Fragment key={key}>{line}</React.Fragment>];
    }

    return [
      <React.Fragment key={`${key}-br`}>
        <br />
        {line}
      </React.Fragment>,
    ];
  });
};

export const LinkifiedText: React.FC<LinkifiedTextProps> = ({
  text,
  className,
  linkClassName,
}) => {
  const content = useMemo(() => {
    if (!text) return null;

    const elements: React.ReactNode[] = [];
    const urlRegex = /https?:\/\/[^\s<>"{}|\\^`[\]]+/g;
    let lastIndex = 0;
    let matchIndex = 0;

    for (const match of text.matchAll(urlRegex)) {
      const url = match[0];
      const start = match.index ?? 0;

      if (start > lastIndex) {
        const rawSegment = text.slice(lastIndex, start);
        elements.push(
          ...renderTextSegment(
            rawSegment,
            `segment-${matchIndex}-${lastIndex}`,
          ),
        );
      }

      const safeUrl = isSafeUrl(url) ? url : null;
      if (safeUrl) {
        elements.push(
          <a
            key={`link-${matchIndex}-${start}`}
            href={safeUrl}
            className={cn(
              "break-all text-blue-600 hover:underline",
              linkClassName,
            )}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            {url}
          </a>,
        );
      } else {
        elements.push(
          ...renderTextSegment(url, `unsafe-${matchIndex}-${start}`),
        );
      }

      lastIndex = start + url.length;
      matchIndex += 1;
    }

    if (lastIndex < text.length) {
      elements.push(
        ...renderTextSegment(text.slice(lastIndex), `tail-${lastIndex}`),
      );
    }

    return elements;
  }, [text, linkClassName]);

  if (!content) {
    return null;
  }

  return (
    <span className={cn("whitespace-pre-wrap break-words", className)}>
      {content}
    </span>
  );
};

export default LinkifiedText;
